import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import { scale, scaleVertically } from '../../utils/scale.utils';
import SearchTypeCheckbox from '../TripSearchWidget/SearchTypeCheckbox';
import ExpenseCamera from './ExpenseCamera';
import ExpenseCharges from './ExpenseCharges';
import { useDispatch, useSelector } from 'react-redux';
import { selectExpenseCategories } from '../../store/selectors/expense.selector';
import { getExpenseCategories } from '../../store/actions/expense';
import Camera from '../Camera';
import { Modals } from '../../constants/ScreenNames';
import { i18nNS, useComponentTKey } from '../../i18n';
import { useTranslation } from 'react-i18next';
import {
  EXPENSE_ACTION_TYPES,
  EXPENSE_RECEIPTS_SUB_TAB_OPTIONS,
  EXPENSE_SUB_TAB_OPTIONS,
} from '../../constants/Enums';
import Button, { BUTTON_TYPES } from '../Button';
import { useReceiptSelection } from '../../utils/useReceiptSelection';
import { PDF_MIME_TYPE } from '../../utils/image.utils';
import useExpenseFormStepOne from './useExpenseFormStepOne';
import ExpenseCar from './ExpenseCar';

const { RECEIPTS, CHARGES, MILEAGE, MANUAL } = EXPENSE_SUB_TAB_OPTIONS;

const ExpenseWidget = ({
  option,
  chargeSheetActions,
  expenseChargesRef,
  fromExistingReport = false,
}) => {
  const [selectedType, setSelectedType] = useState(option || CHARGES);
  const [enableCamera, setEnableCamera] = useState(false);
  const navigation = useNavigation();
  const expenseCategories = useSelector(selectExpenseCategories);
  const dispatch = useDispatch();
  const actionType = EXPENSE_ACTION_TYPES.CREATION;
  const { selectedImages, setSelectedImages, toggleSelectImage } = useReceiptSelection();
  const [activeTab, setActiveTab] = useState(EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA);
  const isContinueDisabled = selectedImages.length === 0;
  const isMileage = selectedType === MILEAGE;
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseNavigation');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const onUploadsContinue = () => {
    navigation.navigate(Modals.CreateExpenseModal, {
      selectedReceipts: selectedImages.map((image, index) => {
        return { ...image, id: index + 1 };
      }),
      fromReceiptsTab: true,
      subTab: EXPENSE_SUB_TAB_OPTIONS.RECEIPTS,
    });
  };

  useEffect(() => {
    if (!expenseCategories) {
      dispatch(getExpenseCategories());
    }
  }, [dispatch, expenseCategories]);

  const { stepOneForm, stepOneContinueButton } = useExpenseFormStepOne({
    isCreationForm: true,
    subTab: isMileage ? MILEAGE : MANUAL,
    fromExistingReport,
    continueButtonStyle: styles.submitButton,
    isMileage,
  });

  return (
    <View style={CommonStyles.mx4}>
      <View testID='ExpenseTabsMain' style={styles.searchTypeRowWrap}>
        <View style={styles.searchTypeRow}>
          <SearchTypeCheckbox
            selected={selectedType === CHARGES}
            iconName="payment"
            onPress={() => setSelectedType(CHARGES)}
            iconLabel={t(tKey('Card'))}
          />
          <SearchTypeCheckbox
            selected={selectedType === RECEIPTS}
            iconName="camera"
            onPress={() => setSelectedType(RECEIPTS)}
            iconLabel={t(tKey('Camera'))}
          />
          <SearchTypeCheckbox
            selected={selectedType === MANUAL}
            iconName="clip"
            onPress={() => setSelectedType(MANUAL)}
            iconLabel={t(tKey('Manual'))}
          />
          <SearchTypeCheckbox
            selected={selectedType === MILEAGE}
            iconName="car"
            onPress={() => setSelectedType(MILEAGE)}
            iconLabel={t(tKey('Car'))}
            isExpense={true}
            style={styles.searchTypeLastChild}
          />
        </View>
      </View>

      {selectedType === CHARGES ? (
        <ExpenseCharges
          chargeSheetActions={chargeSheetActions}
          ref={expenseChargesRef}
          fromExistingReport={fromExistingReport}
        />
      ) : null}
      {selectedType === RECEIPTS ? (
        <ExpenseCamera
          activeTab={activeTab}
          onTabChange={handleTabChange}
          selectedImages={selectedImages}
          toggleSelectImage={toggleSelectImage}
          resetSelectedImages={() => setSelectedImages([])}
          setEnableCamera={setEnableCamera}
        />
      ) : null}
      {selectedType === MANUAL ? stepOneForm : null}
      {selectedType === MILEAGE ? (
        <ExpenseCar
          stepOneForm={stepOneForm}
          stepOneContinueButton={stepOneContinueButton}
          isCreationForm={true}
          isEditable={true}
          subTab={MILEAGE}
        />
      ) : null}

      {enableCamera ? (
        <Camera
          showRotate
          showGallery
          onClose={() => setEnableCamera(false)}
          onGetImage={(data) => {
            setEnableCamera(false);
            if (selectedType === MANUAL) {
              navigation.navigate(Modals.ReceiptScannerModal, {
                file: data.base64,
                actionType,
                subTab: MANUAL,
                isPDF: data.mimeType === PDF_MIME_TYPE,
              });
            } else {
              navigation.navigate(Modals.CreateExpenseModal, {
                file: { base64: data.base64, isPDF: data.mimeType === PDF_MIME_TYPE },
                fromReceiptsTab: true,
                subTab: RECEIPTS,
              });
            }
          }}
          isExpense={true}
        />
      ) : null}
      {selectedType === RECEIPTS && activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.CAMERA && (
        <Button
          label="Continue"
          labelStyle={CommonStyles.buttonFont}
          style={styles.submitButton}
          onPress={() => setEnableCamera(true)}
          type={BUTTON_TYPES.CONTAINED}
        />
      )}
      {selectedType === RECEIPTS && activeTab === EXPENSE_RECEIPTS_SUB_TAB_OPTIONS.UPLOADS && (
        <Button
          label="Continue"
          labelStyle={[
            CommonStyles.buttonFont,
            isContinueDisabled && {
              color: Color.disabledText,
            },
          ]}
          style={{
            ...styles.submitButton,
            ...(isContinueDisabled && {
              backgroundColor: Color.lightgray,
            }),
          }}
          onPress={onUploadsContinue}
          type={BUTTON_TYPES.CONTAINED}
          disabled={isContinueDisabled}
        />
      )}
      {selectedType === MANUAL && stepOneContinueButton}
    </View>
  );
};

const base = scaleVertically(12 * 0.8);
const styles = StyleSheet.create({
  searchTypeRowWrap: {
    ...CommonProperties.mt2,
    ...CommonProperties.mb3,
    ...CommonProperties.borderRadius2,
    ...CommonProperties.shadow,
  },
  searchTypeRow: {
    height: scale(65),
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: Color.accent,
    overflow: 'hidden',
    ...CommonProperties.borderRadius2,
  },
  searchTypeLastChild: {
    borderRightWidth: 0,
  },
  submitButton: {
    marginTop: base * 3 - 2,
    ...CommonProperties.mb5,
    ...CommonProperties.py2,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
  },
  disabledButton: {
    backgroundColor: Color.grayText2,
  },
});

export default ExpenseWidget;
